import { useState, useEffect } from "react";

const C = {
  bg: "#FAF7F2", surface: "#FFFFFF", surfaceAlt: "#F5F0E8", card: "#FFFFFF",
  border: "#E8E0D4", borderLight: "#F0EBE3", text: "#1A1A1A", textMid: "#5C564E",
  textMuted: "#9A9389", accent: "#E8593C", accentSoft: "rgba(232,89,60,0.08)",
  gold: "#D4920A", goldSoft: "rgba(212,146,10,0.08)", green: "#2D8C3C",
  greenSoft: "rgba(45,140,60,0.08)", red: "#DC2626", redSoft: "rgba(220,38,38,0.08)",
};

const ManualOrderForm = ({ onClose, onCreated, api }) => {
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [cart, setCart] = useState([]);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerAddress, setCustomerAddress] = useState("");
  const [orderType, setOrderType] = useState("retirada");
  const [paymentMethod, setPaymentMethod] = useState("dinheiro");
  const [paymentChangeFor, setPaymentChangeFor] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      const cats = await api.get("/api/menu/categories");
      if (cats && cats.length > 0) {
        setCategories(cats);
        setActiveCat(cats[0].id);
      }
    };
    load();
  }, [api]);

  useEffect(() => {
    if (!activeCat) return;
    const load = async () => {
      const data = await api.get(`/api/menu/items?category_id=${activeCat}`);
      setItems((data || []).filter(i => i.is_active));
    };
    load();
  }, [activeCat, api]);

  const addToCart = (item) => {
    const existing = cart.find(c => c.menu_item_id === item.id);
    if (existing) {
      setCart(cart.map(c => c.menu_item_id === item.id ? { ...c, quantity: c.quantity + 1 } : c));
    } else {
      setCart([...cart, { menu_item_id: item.id, name: item.name, price: parseFloat(item.promo_price || item.price), quantity: 1, notes: "" }]);
    }
  };

  const updateQty = (menuItemId, delta) => {
    setCart(cart.map(c => {
      if (c.menu_item_id !== menuItemId) return c;
      const newQty = c.quantity + delta;
      return newQty > 0 ? { ...c, quantity: newQty } : c;
    }).filter(c => c.quantity > 0));
  };

  const removeFromCart = (menuItemId) => {
    setCart(cart.filter(c => c.menu_item_id !== menuItemId));
  };

  const updateItemNotes = (menuItemId, notes) => {
    setCart(cart.map(c => c.menu_item_id === menuItemId ? { ...c, notes } : c));
  };

  const subtotal = cart.reduce((sum, c) => sum + c.price * c.quantity, 0);

  const handleSubmit = async () => {
    if (cart.length === 0) { setError("Adicione pelo menos 1 item"); return; }
    setSaving(true); setError("");
    try {
      const body = {
        customer_name: customerName || "Cliente balcão",
        customer_phone: customerPhone,
        customer_address: orderType === "delivery" ? customerAddress : "",
        order_type: orderType,
        payment_method: paymentMethod,
        payment_change_for: paymentChangeFor ? parseFloat(paymentChangeFor) : null,
        notes,
        items: cart.map(c => ({ menu_item_id: c.menu_item_id, quantity: c.quantity, notes: c.notes })),
      };
      const res = await api.post("/api/orders/manual", body);
      if (res && res.id) {
        onCreated(res);
      } else {
        setError(res?.error || "Erro ao criar pedido");
      }
    } catch (e) {
      setError("Erro de conexão");
    }
    setSaving(false);
  };

  const inputStyle = {
    width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${C.border}`,
    fontSize: 13, fontFamily: "'DM Sans', sans-serif", background: C.bg, outline: "none", boxSizing: "border-box",
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <div style={{ background: C.surface, borderRadius: 16, width: 820, maxHeight: "90vh", overflow: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.15)" }}>
        {/* Header */}
        <div style={{ padding: "20px 24px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700, color: C.text, margin: 0 }}>Novo Pedido Manual</h2>
            <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>Pedido registrado diretamente no painel</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 22, color: C.textMuted, cursor: "pointer", padding: 4 }}>✕</button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", minHeight: 400 }}>
          {/* Lado esquerdo — Cardápio */}
          <div style={{ padding: 20, borderRight: `1px solid ${C.border}` }}>
            {/* Categorias */}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
              {categories.map(cat => (
                <button key={cat.id} onClick={() => setActiveCat(cat.id)} style={{
                  padding: "6px 14px", borderRadius: 8, border: `1px solid ${activeCat === cat.id ? C.accent : C.border}`,
                  background: activeCat === cat.id ? C.accentSoft : C.surface, color: activeCat === cat.id ? C.accent : C.textMid,
                  fontSize: 12, fontWeight: 600, cursor: "pointer",
                }}>{cat.icon} {cat.name}</button>
              ))}
            </div>
            {/* Itens */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {items.map(item => {
                const inCart = cart.find(c => c.menu_item_id === item.id);
                return (
                  <div key={item.id} onClick={() => addToCart(item)} style={{
                    padding: "12px 14px", borderRadius: 10, border: `1px solid ${inCart ? C.accent : C.border}`,
                    background: inCart ? C.accentSoft : C.card, cursor: "pointer", transition: "all 0.15s",
                  }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{item.name}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: C.accent, marginTop: 4 }}>R$ {Number(item.promo_price || item.price).toFixed(2)}</div>
                    {inCart && <div style={{ fontSize: 11, color: C.accent, marginTop: 4, fontWeight: 600 }}>{inCart.quantity}x no pedido</div>}
                  </div>
                );
              })}
              {items.length === 0 && <div style={{ gridColumn: "1/-1", padding: 20, textAlign: "center", color: C.textMuted, fontSize: 13 }}>Selecione uma categoria</div>}
            </div>
          </div>

          {/* Lado direito — Carrinho + Dados */}
          <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Carrinho */}
            <div>
              <div style={{ fontSize: 11, color: C.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Itens do pedido</div>
              {cart.length === 0 ? (
                <div style={{ padding: 16, textAlign: "center", color: C.textMuted, fontSize: 12, background: C.surfaceAlt, borderRadius: 8 }}>Clique nos itens para adicionar</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {cart.map(c => (
                    <div key={c.menu_item_id} style={{ padding: "8px 10px", background: C.surfaceAlt, borderRadius: 8 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{c.name}</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>R$ {(c.price * c.quantity).toFixed(2)}</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                        <button onClick={() => updateQty(c.menu_item_id, -1)} style={{ width: 26, height: 26, borderRadius: 6, border: `1px solid ${C.border}`, background: C.surface, cursor: "pointer", fontSize: 14 }}>−</button>
                        <span style={{ fontSize: 13, fontWeight: 600, minWidth: 20, textAlign: "center" }}>{c.quantity}</span>
                        <button onClick={() => updateQty(c.menu_item_id, 1)} style={{ width: 26, height: 26, borderRadius: 6, border: `1px solid ${C.border}`, background: C.surface, cursor: "pointer", fontSize: 14 }}>+</button>
                        <button onClick={() => removeFromCart(c.menu_item_id)} style={{ marginLeft: "auto", background: "none", border: "none", color: C.red, cursor: "pointer", fontSize: 12 }}>remover</button>
                      </div>
                      <input value={c.notes} onChange={e => updateItemNotes(c.menu_item_id, e.target.value)} placeholder="Obs do item..." style={{ ...inputStyle, marginTop: 6, padding: "6px 8px", fontSize: 11 }} />
                    </div>
                  ))}
                </div>
              )}
              {cart.length > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, padding: "10px 0", borderTop: `1px solid ${C.border}`, fontSize: 16, fontWeight: 700, color: C.text }}>
                  <span>Subtotal</span><span>R$ {subtotal.toFixed(2)}</span>
                </div>
              )}
            </div>

            {/* Dados do cliente */}
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ fontSize: 11, color: C.textMuted, textTransform: "uppercase", letterSpacing: 1 }}>Dados do cliente</div>
              <input value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="Nome (opcional)" style={inputStyle} />
              <input value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} placeholder="Telefone (opcional)" style={inputStyle} />
              <div style={{ display: "flex", gap: 8 }}>
                {["retirada", "delivery"].map(t => (
                  <button key={t} onClick={() => setOrderType(t)} style={{
                    flex: 1, padding: "8px 0", borderRadius: 8, border: `1px solid ${orderType === t ? C.accent : C.border}`,
                    background: orderType === t ? C.accentSoft : C.surface, color: orderType === t ? C.accent : C.textMid,
                    fontSize: 12, fontWeight: 600, cursor: "pointer",
                  }}>{t === "retirada" ? "🏪 Retirada" : "🛵 Delivery"}</button>
                ))}
              </div>
              {orderType === "delivery" && (
                <input value={customerAddress} onChange={e => setCustomerAddress(e.target.value)} placeholder="Endereço de entrega" style={inputStyle} />
              )}
              <div style={{ display: "flex", gap: 8 }}>
                {[{ k: "dinheiro", l: "💵 Dinheiro" }, { k: "cartao", l: "💳 Cartão" }, { k: "pix", l: "📱 PIX" }].map(p => (
                  <button key={p.k} onClick={() => setPaymentMethod(p.k)} style={{
                    flex: 1, padding: "8px 0", borderRadius: 8, border: `1px solid ${paymentMethod === p.k ? C.accent : C.border}`,
                    background: paymentMethod === p.k ? C.accentSoft : C.surface, color: paymentMethod === p.k ? C.accent : C.textMid,
                    fontSize: 11, fontWeight: 600, cursor: "pointer",
                  }}>{p.l}</button>
                ))}
              </div>
              {paymentMethod === "dinheiro" && (
                <input value={paymentChangeFor} onChange={e => setPaymentChangeFor(e.target.value)} placeholder="Troco para R$..." type="number" style={inputStyle} />
              )}
              <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Observações gerais..." style={inputStyle} />
            </div>

            {error && <div style={{ background: C.redSoft, color: C.red, padding: "8px 12px", borderRadius: 8, fontSize: 12, textAlign: "center" }}>{error}</div>}

            <button onClick={handleSubmit} disabled={saving || cart.length === 0} style={{
              padding: "12px 0", borderRadius: 10, border: "none", background: cart.length > 0 ? C.accent : C.border,
              color: "#fff", fontSize: 14, fontWeight: 600, cursor: saving ? "wait" : "pointer",
              boxShadow: cart.length > 0 ? `0 4px 16px ${C.accent}40` : "none", opacity: saving ? 0.7 : 1,
            }}>{saving ? "Criando..." : "✓ Criar Pedido"}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManualOrderForm;
